import React, { useState } from 'react';
import { Box } from '@mui/material';
import { Pie } from 'react-chartjs-2';
import { AttendanceStateDto, CitizenAttendanceStatisticsSummaryDto } from '../../../../api-service/clients';
import { ColorPalette } from '../../../../components/config';
import { getHoursAndMinutes } from '../../utils';
import { Container } from '../../../../components/container';
import { Cell } from '../../../../components/cell';
import { Row } from '../../../../components/row';
import { getAttendanceStateTextColor, getStatisticsStateColor } from '../../components/attendance-protocol/utils';

interface IAttendanceStatisticsResultSummary {
  summary: CitizenAttendanceStatisticsSummaryDto;
}

export function AttendanceStatisticsResultSummary(props: IAttendanceStatisticsResultSummary) {
  const { summary } = props;

  const { onTimeCount, notOnTimeCount, absenceWithReasonCount, notShownCount, notRelevantCount, totalExpectedTimeInMinutes, totalActualTimeInMinutes, noShowFullDayCount } =
    summary;

  const attendanceChecks = [
    { header: 'Til tiden', shortHeader: 'Til tiden', attendanceState: AttendanceStateDto.OnTime, count: onTimeCount },
    { header: 'Ikke til tiden', shortHeader: 'Ikke til tiden', attendanceState: AttendanceStateDto.NotOnTime, count: notOnTimeCount },
    { header: 'Udeblevet (Lovlig grund)', shortHeader: 'Udeblevet (lovlig)', attendanceState: AttendanceStateDto.AbsenceWithReason, count: absenceWithReasonCount },
    { header: 'Udeblevet (ikke rimelig grund)', shortHeader: 'Udeblevet', attendanceState: AttendanceStateDto.NotShown, count: notShownCount },
  ];

  const totalCount = attendanceChecks.reduce((total, check) => total + check.count, 0);

  return (
    <Box sx={{ border: `solid 1px ${ColorPalette.DefaultTextColor}`, borderLeft: `solid 5px ${ColorPalette.CityNightSkiesDark}`, padding: 2 }}>
      <Container>
        <Row>
          <Cell size="quarters">
            <Box>
              <Box sx={{ fontWeight: 600, fontSize: '0.875em' }}>Fremmødetjek i %</Box>
              {attendanceChecks.map(check => (
                <PercentRow key={check.header} header={check.header} attendanceState={check.attendanceState} count={check.count} totalCount={totalCount} />
              ))}
            </Box>
          </Cell>
          <Cell size="quarters">
            <Box sx={{ marginBottom: 1 }}>
              <Box sx={{ fontWeight: 600, fontSize: '0.875em' }}>Samlet forventet deltagelse</Box>
              <Box sx={{ fontSize: '0.875em' }}>{getHoursAndMinutes(totalExpectedTimeInMinutes)}</Box>
            </Box>
            <Box>
              <Box sx={{ fontWeight: 600, fontSize: '0.875em' }}>Samlet faktisk deltagelse</Box>
              <Box sx={{ fontSize: '0.875em' }}>{getHoursAndMinutes(totalActualTimeInMinutes)}</Box>
            </Box>
          </Cell>
          <Cell size="quarters">
            <Box sx={{ marginBottom: 1 }}>
              <Box sx={{ fontWeight: 600, fontSize: '0.875em' }}>Udeblevet antal hele dage</Box>
              <Box sx={{ fontSize: '0.875em' }}>{`${noShowFullDayCount} dag${noShowFullDayCount !== 1 ? 'e' : ''}`} </Box>
            </Box>
            <Box>
              <Box sx={{ fontWeight: 600, fontSize: '0.875em' }}>Fremmøderegistrering ikke relevant</Box>
              <Box sx={{ fontSize: '0.875em' }}>{`${notRelevantCount} lektion${notRelevantCount !== 1 ? 'er' : ''}`} </Box>
            </Box>
          </Cell>
          <Cell size="quarters">
            <AttendanceCheckPie attendanceChecks={attendanceChecks} totalCount={totalCount} />
          </Cell>
        </Row>
      </Container>
    </Box>
  );
}

interface IAttendanceCheck {
  header: string;
  // The cell is a quarter of the row, so the legend beside the pie uses a shorter wording.
  shortHeader: string;
  attendanceState: AttendanceStateDto;
  count: number;
}

interface IPieTooltip {
  left: number;
  top: number;
  text: string;
}

interface IChartTooltipModel {
  opacity: number;
  caretX: number;
  caretY: number;
  dataPoints?: { index: number }[];
}

interface IAttendanceCheckPieProps {
  attendanceChecks: IAttendanceCheck[];
  totalCount: number;
}

function AttendanceCheckPie(props: IAttendanceCheckPieProps) {
  const { attendanceChecks, totalCount } = props;
  const [tooltip, setTooltip] = useState<IPieTooltip>();

  if (totalCount === 0) {
    return null;
  }

  const data = {
    labels: attendanceChecks.map(check => check.header),
    datasets: [
      {
        data: attendanceChecks.map(check => check.count),
        backgroundColor: attendanceChecks.map(check => getStatisticsStateColor(check.attendanceState)),
      },
    ],
  };

  const options = {
    maintainAspectRatio: false,
    // Drawn as markup below instead, so the labels can line up with the top of the pie.
    legend: { display: false },
    plugins: {
      datalabels: { display: false },
    },
    // Chart.js draws its own tooltip inside the canvas, where the full text would be cut off.
    tooltips: {
      enabled: false,
      custom: (model: IChartTooltipModel) => setTooltip(current => nextTooltip(model, attendanceChecks, totalCount, current)),
    },
  };

  return (
    <Box display="flex" justifyContent="center" alignItems="flex-start" gap={1}>
      <Box position="relative" height={124} width={124} flexShrink={0}>
        <Pie data={data} options={options} />
        {tooltip && (
          <Box
            sx={{
              position: 'absolute',
              left: tooltip.left,
              top: tooltip.top,
              transform: 'translate(-50%, -130%)',
              zIndex: theme => theme.zIndex.tooltip,
              backgroundColor: ColorPalette.DefaultTextColor,
              color: ColorPalette.WHITE,
              fontSize: '0.875em',
              padding: '2px 6px',
              borderRadius: 1,
              whiteSpace: 'nowrap',
              pointerEvents: 'none',
            }}
          >
            {tooltip.text}
          </Box>
        )}
      </Box>
      <Box>
        {attendanceChecks.map(check => (
          <Box key={check.header} display="flex" alignItems="center" sx={{ fontSize: '0.875em' }}>
            <Box sx={{ backgroundColor: getStatisticsStateColor(check.attendanceState), width: 12, height: 12, marginRight: 0.5, flexShrink: 0 }} />
            {check.shortHeader}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

// Chart.js calls custom on every draw, so an unchanged tooltip keeps the previous object and React skips the re-render.
function nextTooltip(model: IChartTooltipModel, attendanceChecks: IAttendanceCheck[], totalCount: number, current?: IPieTooltip) {
  const hoveredIndex = model.dataPoints?.[0]?.index;
  if (!model.opacity || hoveredIndex === undefined) {
    return undefined;
  }

  const check = attendanceChecks[hoveredIndex];
  const next = {
    left: model.caretX,
    top: model.caretY,
    text: `${check.header}: ${check.count} (${Math.round((check.count / totalCount) * 100)} %)`,
  };

  return current && current.left === next.left && current.top === next.top && current.text === next.text ? current : next;
}

interface IPercentRowProps {
  header: string;
  attendanceState: AttendanceStateDto;
  count: number;
  totalCount: number;
}

function PercentRow(props: IPercentRowProps) {
  const { header, attendanceState, count, totalCount } = props;

  return (
    <Box display="flex" maxWidth={266} alignItems="flex-start">
      <Box sx={{ fontSize: '0.875em', flex: 1 }}>{header}</Box>
      <Box
        sx={{
          fontSize: '0.875em',
          backgroundColor: getStatisticsStateColor(attendanceState),
          width: '60px',
          padding: '0 5px',
          color: getAttendanceStateTextColor(attendanceState),
          textAlign: 'right',
        }}
      >
        {totalCount === 0 ? '0 %' : `${Math.round((count / totalCount) * 100)} %`}
      </Box>
    </Box>
  );
}
